"use client";

import { useMemo, useState } from "react";
import { Frame, Slider, Verdict, Note, money } from "./parts";

const L = {
    fr: {
        title: "Combien de ventes pour couvrir vos charges",
        fixed: "Charges fixes par mois",
        price: "Prix de vente",
        variable: "Coût variable par vente",
        needed: "Ventes nécessaires par mois",
        marginPer: "Marge par vente",
        unreachable: "impossible",
        sales: "ventes",
        revenue: "Recettes",
        costs: "Coûts",
        good: "Le seuil est atteignable. Chaque vente au-delà est du bénéfice.",
        warn: "Le seuil demande un volume soutenu. Regardez d'abord vos charges fixes.",
        bad: "La marge par vente ne couvre pas le coût variable : aucun volume ne rendra ce modèle rentable.",
        note: "Essayez de baisser les charges fixes de 20 %, puis de monter le prix de 20 %. Le seuil ne bouge pas de la même façon : les charges fixes le déplacent proportionnellement, le prix agit à travers la marge, donc plus fort quand le coût variable est faible.",
    },
    en: {
        title: "How many sales cover your costs",
        fixed: "Fixed costs per month",
        price: "Sale price",
        variable: "Variable cost per sale",
        needed: "Sales needed per month",
        marginPer: "Margin per sale",
        unreachable: "impossible",
        sales: "sales",
        revenue: "Revenue",
        costs: "Costs",
        good: "The threshold is reachable. Every sale beyond it is profit.",
        warn: "The threshold demands sustained volume. Look at your fixed costs first.",
        bad: "Margin per sale does not cover the variable cost: no volume will make this model profitable.",
        note: "Try cutting fixed costs by 20 percent, then raising the price by 20 percent. The threshold does not move the same way: fixed costs shift it proportionally, price acts through margin, so it bites harder when variable cost is low.",
    },
};

const W = 560;
const H = 220;
const PAD = { l: 52, r: 12, t: 12, b: 26 };

export default function BreakEvenChart({ lang = "fr" }) {
    const t = L[lang] || L.fr;

    const [fixed, setFixed] = useState(1800);
    const [price, setPrice] = useState(60);
    const [variable, setVariable] = useState(15);

    const r = useMemo(() => {
        const margin = price - variable;
        const needed = margin > 0 ? fixed / margin : Infinity;
        // L'axe couvre une fois et demie le seuil, pour que le croisement
        // tombe aux deux tiers du cadre plutôt que dans un coin.
        const span = Number.isFinite(needed) ? Math.max(needed * 1.5, 4) : 60;
        const max = Math.max(price * span, fixed + variable * span, 1);
        return { margin, needed, span, max };
    }, [fixed, price, variable]);

    const x = (n) => PAD.l + (n / r.span) * (W - PAD.l - PAD.r);
    const y = (v) => H - PAD.b - (v / r.max) * (H - PAD.t - PAD.b);

    const tone = r.margin <= 0 ? "bad" : r.needed > 200 ? "warn" : "good";

    return (
        <Frame icon="chart-square-linear" title={t.title}>
            <div className="grid sm:grid-cols-[1fr_auto] gap-5 items-center">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={t.title}>
                    <line x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.2" />
                    <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.2" />

                    {/* Coûts : charges fixes plus coût variable */}
                    <line x1={x(0)} y1={y(fixed)} x2={x(r.span)} y2={y(fixed + variable * r.span)} stroke="currentColor" strokeOpacity="0.45" strokeWidth="2" strokeDasharray="5 4" />
                    <text x={PAD.l + 6} y={y(fixed) - 6} fontSize="10" fill="currentColor" fillOpacity="0.6">{t.costs}</text>

                    {/* Recettes */}
                    <line x1={x(0)} y1={y(0)} x2={x(r.span)} y2={y(price * r.span)} stroke="#E8511D" strokeWidth="2.5" />
                    <text x={W - PAD.r - 4} y={y(price * r.span) + 14} textAnchor="end" fontSize="10" fill="#E8511D">{t.revenue}</text>

                    {/* Seuil */}
                    {r.margin > 0 && r.needed <= r.span && (
                        <>
                            <line x1={x(r.needed)} y1={y(price * r.needed)} x2={x(r.needed)} y2={H - PAD.b} stroke="currentColor" strokeOpacity="0.3" strokeDasharray="3 3" />
                            <circle cx={x(r.needed)} cy={y(price * r.needed)} r="5.5" fill="none" stroke="#E8511D" strokeWidth="2" />
                            <text x={x(r.needed)} y={H - 8} textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity="0.75">
                                {Math.ceil(r.needed)} {t.sales}
                            </text>
                        </>
                    )}

                    <text x={PAD.l - 6} y={y(fixed) + 3} textAnchor="end" fontSize="10" fill="currentColor" fillOpacity="0.6">
                        {Math.round(fixed)}
                    </text>
                </svg>

                <div className="space-y-4 sm:w-56">
                    <Slider label={t.fixed} value={fixed} onChange={setFixed} min={200} max={6000} step={100} display={money(fixed, lang)} />
                    <Slider label={t.price} value={price} onChange={setPrice} min={5} max={300} step={5} display={money(price, lang)} />
                    <Slider label={t.variable} value={variable} onChange={setVariable} min={0} max={150} step={5} display={money(variable, lang)} />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
                <div className="rounded-xl bg-primary/[0.06] dark:bg-primary/10 p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.needed}</div>
                    <div className="text-2xl font-bold text-primary tabular-nums">
                        {r.margin > 0 ? Math.ceil(r.needed).toLocaleString(lang === "fr" ? "fr-FR" : "en-US") : t.unreachable}
                    </div>
                </div>
                <div className="rounded-xl bg-gray-100/70 dark:bg-white/[0.04] p-4">
                    <div className="text-sm text-gray-600 dark:text-lightBlue mb-0">{t.marginPer}</div>
                    <div className="text-2xl font-bold tabular-nums dark:text-white">{money(r.margin, lang)}</div>
                </div>
            </div>

            <Verdict tone={tone}>{t[tone]}</Verdict>
            <Note>{t.note}</Note>
        </Frame>
    );
}
